<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="layers">
  <h1>{{ $t('annotation-layers') }}</h1>
  <b-message v-if="error" type="is-danger" has-icon icon-size="is-small" size="is-small">
    <p> {{ $t('unexpected-error-info-message') }} </p>
  </b-message>
  <template v-else>
    <b-field>
      <b-select :placeholder="$t('select-layer')" size="is-small" v-model="selectedLayer">
        <option v-for="layer in unselectedLayers" :value="layer" :key="layer.id">
          {{ layerName(layer) }}
        </option>
      </b-select>
      <button class="button is-small" @click="addLayer()" :disabled="!selectedLayer">{{ $t('button-add') }}</button>
    </b-field>
    <table class="table">
      <thead>
        <tr>
          <th class="checkbox-column"><span class="far fa-eye"></span></th>
          <th v-if="!reviewMode" class="checkbox-column"><span class="fas fa-pencil-alt"></span></th>
          <th class="name-column"></th>
          <th class="checkbox-column"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(layer, idx) in selectedLayers" :key="layer.id">
          <td class="checkbox-column">
            <b-checkbox size="is-small" :value="layer.visible" @input="toggleLayerVisibility(idx)" />
          </td>
          <td v-if="!reviewMode" class="checkbox-column">
            <b-checkbox size="is-small" :value="layer.drawOn" :disabled="!canDraw(layer)" @input="toggleLayerDrawOn(idx)" />
          </td>

          <td class="name-column">
            {{ layerName(layer) }}
          </td>
          <td class="checkbox-column">
            <button v-if="!reviewMode || !layer.isReview" class="button is-small" @click="removeLayer(idx)">
              <span class="fas fa-times"></span>
            </button>
          </td>
        </tr>
        <tr v-if="selectedLayers.length === 0">
          <td colspan="4" class="has-text-grey is-italic">{{$t('no-selected-layers')}}</td>
        </tr>
      </tbody>
    </table>

    <div class="opacity">
      <label>{{ $t('layers-opacity') }}</label>
      <input class="slider is-fullwidth is-small" v-model="layersOpacity" step="0.05" min="0" max="1" type="range">
    </div>
  </template>
</div>
</template>

<script>
import _ from 'lodash';
import {get} from '@/utils/store-helpers';
import {fullName} from '@/utils/user-utils.js';
import {Cytomine} from 'cytomine-client';

export default {
  name: 'layers-panel',
  props: {
    index: String,
    layersToPreload: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      error: false,
      layers: [],
      indexLayers: [],
      selectedLayer: null,
      restoringLayers: false,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    project: get('currentProject/project'),
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    imageWrapper() {
      return this.viewerWrapper.images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    activePanel() {
      return this.viewerWrapper.activePanel;
    },
    layersOpacity: {
      get() {
        return this.imageWrapper.style.layersOpacity;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setLayersOpacity', Number(value));
      }
    },
    selectedLayers() {
      return this.imageWrapper.layers.selectedLayers || [];
    },
    selectedLayersIds() {
      return this.selectedLayers.map(layer => layer.id);
    },
    unselectedLayers() {
      return this.layers
        .filter(layer => !this.selectedLayersIds.includes(layer.id))
        .sort((a, b) => (a.lastname < b.lastname) ? -1 : 1 );
    },
    nbReviewedAnnotations() {
      return this.indexLayers.reduce((cnt, layer) => cnt + layer.countReviewedAnnotation, 0);
    },
    hasReviewLayer() {
      return this.image.inReview || this.image.reviewed;
    },
    reviewLayer() {
      return {id: -1, isReview: true};
    },
    reviewMode() {
      return this.imageWrapper.review.reviewMode;
    },
    isActiveImage() {
      return this.viewerWrapper.activeImage === this.index;
    },
    storageKey() {
      if (!this.project || !this.image || !this.currentUser) {
        return null;
      }
      return `anno-layers-${this.project.id}-${this.image.id}-${this.currentUser.id}`;
    }
  },
  watch: {
    activePanel() {
      this.fetchIndexLayers(true); // 패널이 열릴 때 강제로 다시 읽어옴
    },

    reviewMode() {
      if(this.reviewMode) {
        if(!this.layers.includes(this.reviewLayer)) {
          this.layers.push(this.reviewLayer);
        }
        this.addLayer(this.reviewLayer);
      }
      else {
        if(!this.hasReviewLayer) {
          let index = this.selectedLayersIds.findIndex(id => id === this.reviewLayer.id);
          if(index !== -1) {
            this.removeLayer(index);
          }

          this.layers = this.layers.filter(layer => !layer.isReview);
        }
      }
    },

    layersToPreload: {
      deep: true,
      handler(layersToPreload) {
        layersToPreload.forEach(layerId => {
          let index = this.selectedLayersIds.findIndex(id => id === layerId);
          if(index !== -1) {
            if (!this.selectedLayers[index].visible) {
              this.toggleLayerVisibility(index);
            }
            return;
          }

          this.addLayerById(layerId, true, false);
        });
      }
    },

    selectedLayers: {
      deep: true,
      handler(newLayers) {
        if (this.restoringLayers || !this.storageKey) {
          return;
        }

        const payload = newLayers.map(layer => ({
          id: layer.id,
          visible: !!layer.visible,
          drawOn: !!layer.drawOn,
        }));

        localStorage.setItem(this.storageKey, JSON.stringify(payload));
      }
    },

    async storageKey(newVal, oldVal) {
      if (!newVal || newVal === oldVal) {
        return;
      }

      await this.refreshLayersForActiveImage();
    }
  },

  methods: {
    addAnnotationEventHandler(annot, saved = true) {
      this.annotationEventHandler(annot);
      let updatedProject = this.$store.state.currentProject.project.clone();

      if(annot.type === 'UserAnnotation') {
        if(saved) updatedProject.numberOfAnnotations++;
      }
      else {
        updatedProject.numberOfReviewedAnnotations++;
      }

      this.$store.dispatch('currentProject/updateProject', updatedProject);
    },

    deleteAnnotationEventHandler(annot) {
      this.annotationEventHandler(annot);
    },

    annotationEventHandler(annot) {
      if(annot.image === this.image.id) {
        setTimeout(() => {
          this.fetchIndexLayers(true);
        }, 500);
      }
    },

    reloadAnnotationsHandler({idImage}={}) {
      if(!idImage || idImage === this.image.id) {
        this.fetchIndexLayers();
      }
    },

    layerName(layer) {
      if (!Object.prototype.hasOwnProperty.call(layer, 'user')) {
        return layer.name;
      }

      if (layer.isReview) {
        return `${this.$t('review-layer')} (${this.nbReviewedAnnotations})`;
      }

      let name = fullName(layer);
      let id = (this.currentUser.isDeveloper) ? ` (${this.$t('id')}: ${layer.id})` : '';
      
      let indexLayer = this.indexLayers.find(index => Number(index.user) === Number(layer.id)) || {};
      return `${name}${id} (${indexLayer.countAnnotation || 0})`;
    },

    canDraw(layer) {
      return !layer.isReview && this.$store.getters['currentProject/canEditLayer'](layer.id);
    },

    addLayerById(id, visible = true, drawOn = null) {
      let layer = this.layers.find(layer => layer.id === id);
      if(layer) {
        this.addLayer(layer, visible, drawOn);
      }
    },

    addLayer(layer = this.selectedLayer, visible = true, drawOn = null) {
      if(!layer || this.selectedLayersIds.includes(layer.id)) {
        return;
      }

      layer.visible = visible;
      layer.drawOn = (drawOn === null)
        ? (layer.id === this.currentUser.id && this.canDraw(layer))
        : (!!drawOn && this.canDraw(layer));

      this.$store.dispatch(this.imageModule + 'addLayer', layer);
      this.selectedLayer = null;

      this.fetchIndexLayers(true);
    },

    removeLayer(index) {
      this.$store.dispatch(this.imageModule + 'removeLayer', index);
    },

    clearSelectedLayers() {
      const count = this.selectedLayers.length;
      for (let i = count - 1; i >= 0; i--) {
        this.removeLayer(i);
      }
    },

    toggleLayerVisibility(index) {
      this.$store.dispatch(this.imageModule + 'toggleLayerVisibility', index);
    },

    toggleLayerDrawOn(index) {
      this.$store.commit(this.imageModule + 'toggleLayerDrawOn', index);
    },

    async fetchLayers() {
      this.layers = (await this.project.fetchUserLayers(this.image.id)).array;

      if (this.hasReviewLayer) {
        this.layers.push(this.reviewLayer);
      }

      let layers = (await Cytomine.instance.api.get(`image-instances/${this.image.id}/annotation-layers`)).data;
      this.layers.push(...layers);

      let idLayers = this.layers.map(layer => layer.id);
      this.$store.commit(this.imageModule + 'filterSelectedLayers', idLayers);
    },

    async fetchIndexLayers(force=false) {
      if(!force && this.activePanel !== 'layers') {
        return;
      }

      let indexLayers = await Promise.all(this.slices.map(async slice => await slice.fetchAnnotationsIndex()));
      indexLayers = Object.values(_.groupBy(indexLayers.flat(), 'user'));
      this.indexLayers = indexLayers.map(userIndexLayers => userIndexLayers.reduce((a, b) => {
        return {
          user: a.user,
          countAnnotation: a.countAnnotation + b.countAnnotation,
          countReviewedAnnotation: a.countReviewedAnnotation + b.countReviewedAnnotation
        };
      }, {user: userIndexLayers[0].user, countAnnotation: 0, countReviewedAnnotation: 0}));
    },

    getDefaultLayerState() {
      const layersToAdd = this.layersToPreload.map(id => ({
        id,
        visible: true,
        drawOn: false,
      }));
      const addedIds = layersToAdd.map(layer => layer.id);

      this.layers.forEach(layer => {
        if (!addedIds.includes(layer.id)) {
          layersToAdd.push({
            id: layer.id,
            visible: true,
            drawOn: layer.id === this.currentUser.id && this.canDraw(layer),
          });
          addedIds.push(layer.id);
        }
      });

      return layersToAdd;
    },

    loadSavedLayerState() {
      if (!this.storageKey) {
        return null;
      }

      const saved = localStorage.getItem(this.storageKey);
      if (!saved) {
        return null;
      }

      try {
        const parsed = JSON.parse(saved);

        if (!Array.isArray(parsed)) {
          return null;
        }

        return parsed.map(item => {
          if (typeof item === 'number') {
            return { id: item, visible: true, drawOn: false };
          }

          return {
            id: item.id,
            visible: item.visible !== false,
            drawOn: !!item.drawOn,
          };
        });
      }
      catch (e) {
        console.error('Failed to parse saved layers', e);
        return null;
      }
    },

    async refreshLayersForActiveImage() {
      if (!this.project || !this.image || !this.currentUser) {
        return;
      }

      this.restoringLayers = true;

      try {
        await Promise.all([
          this.fetchLayers(),
          this.fetchIndexLayers(true),
        ]);

        this.clearSelectedLayers();

        let layersToAdd = this.loadSavedLayerState();

        if (!layersToAdd || layersToAdd.length === 0) {
          layersToAdd = this.getDefaultLayerState();
        }

        layersToAdd.forEach(layer => {
          this.addLayerById(layer.id, layer.visible, layer.drawOn);
        });

        const hasActiveDrawLayer = this.selectedLayers.some(layer => layer.drawOn);
        if (!hasActiveDrawLayer) {
          const myLayerIndex = this.selectedLayers.findIndex(
            layer => layer.id === this.currentUser.id && this.canDraw(layer)
          );

          if (myLayerIndex !== -1 && !this.selectedLayers[myLayerIndex].drawOn) {
            this.toggleLayerDrawOn(myLayerIndex);
          }
        }
      }
      finally {
        this.restoringLayers = false;
      }
    },

    shortkeyHandler(key) {
      if(!key.startsWith('toggle-all-') && !this.isActiveImage) {
        return;
      }

      key = key.replace('toggle-all-', 'toggle-');
      if(key === 'toggle-selected-layers') {
        this.selectedLayers.forEach((layer, index) => this.toggleLayerVisibility(index));
      }
      else if(key === 'toggle-review-layer') {
        let index = this.selectedLayersIds.findIndex(id => id === this.reviewLayer.id);
        if(index !== -1) {
          this.toggleLayerVisibility(index);
          return;
        }

        if(this.layers.includes(this.reviewLayer)) {
          this.addLayer(this.reviewLayer);
        }
      }
    }
  },

  async created() {
    try {
      await this.refreshLayersForActiveImage();
    }
    catch(error) {
      console.log(error);
      this.error = true;
      this.$notify({type: 'error', text: this.$t('notif-error-loading-annotation-layers')});
    }
  },

  mounted() {
    this.$eventBus.$on('addAnnotation', this.addAnnotationEventHandler);
    this.$eventBus.$on('deleteAnnotation', this.deleteAnnotationEventHandler);
    this.$eventBus.$on('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
  },

  beforeDestroy() {
    this.$eventBus.$off('addAnnotation', this.addAnnotationEventHandler);
    this.$eventBus.$off('deleteAnnotation', this.deleteAnnotationEventHandler);
    this.$eventBus.$off('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
  }
};
</script>

<style scoped>
>>> select {
  width: 21em;
}

.table {
  margin-bottom: 1em !important;
  font-size: 0.9em;
}

.table tbody {
  display: block;
  overflow: auto;
  max-height: 10em;
}

.table thead tr {
  display: block;
}

td, th {
  padding: 0.25em !important;
  vertical-align: middle !important;
}

td .button {
  width: 1.5em;
  height: 1.5em;
  font-size: 0.9em;
  padding: 0;
}

.checkbox-column {
  min-width: 2.2em;
  text-align: center !important;
}

.name-column {
  width: 100%;
}

>>> .checkbox .control-label {
  padding: 0 !important;
}

>>> input[type="range"].slider {
  margin: 0;
  padding: 0;
}

.opacity {
  display: flex;
  align-items: center;
}

.opacity label {
  text-transform: uppercase;
  font-size: 0.8em;
  width: 15em;
}
</style>
